<script lang="ts">
import { defineComponent } from "vue";
import KlereoSlider from "@/shared/components/KlereoSlider.vue";
import KlereoCheckbox from "@/shared/components/KlereoCheckbox.vue";
import { getProbeType } from "@/shared/utils/probes";
import { injectProbe } from "@/shared/utils/injection";
import { Params, PoolDetails } from "@/models/pool-details.model";
import KlereoSpinner from "@/shared/components/KlereoSpinner.vue";
import { getActiveMode } from "@/shared/utils/outs";
import { isTouchDevice } from "@/shared/utils/misc/is-touch-device";

type Param = "seuilMin" | "seuilMax" | "consigne";

export default defineComponent({
  ...injectProbe,
  components: { KlereoSpinner, KlereoSlider, KlereoCheckbox },
  data() {
    return {
      values: {} as Record<Param, number>,
      loading: 0,
      previousSeuilMin: null as number | null,
      previousSeuilMax: null as number | null,
      previousConsigne: null as number | null,
      editing: !isTouchDevice(),
      editingControls: isTouchDevice(),
    };
  },
  computed: {
    selectedMode() {
      return this.out ? getActiveMode(this.out, this.pool) : undefined;
    },
    consigneValue() {
      const param = this.selectedMode?.consigneName;
      return param
        ? this.pool.params[param as keyof PoolDetails["params"]]
        : -2000;
    },
    probeType() {
      return getProbeType(this.probe.type);
    },
    hasConsigne() {
      return this.selectedMode?.consigneName;
    },
    shouldHideParams() {
      return (
        this.probeType.name === "TYPE_CAPTEUR_BIDON" &&
        (this.pool.access ?? this.token.dac) < 30
      );
    },
  },
  methods: {
    setSeuilMax(value: number) {
      this.probe.seuilMax = value;
      if (
        this.hasConsigne &&
        this.pool.params[this.selectedMode!.consigneName as keyof Params] !==
          -2000
      ) {
        this.probe.seuilMax = Math.max(
          value,
          this.selectedMode!.consigneRange![0] + 2 * this.probeType.gap
        );
        this.pool.params[this.selectedMode!.consigneName as keyof Params] =
          Math.min(
            this.consigneValue,
            this.probe.seuilMax - this.probeType.gap
          );

        const newSeuilMin = Math.min(
          this.probe.seuilMin,
          this.probe.seuilMax - 2 * this.probeType.gap
        );

        if (this.probe.seuilMin === -2000) {
          if (newSeuilMin !== -2000) {
            this.previousSeuilMin = newSeuilMin;
          }
        } else {
          this.probe.seuilMin = newSeuilMin;
        }
      }
    },
    setSeuilMin(value: number) {
      this.probe.seuilMin = value;
      if (
        this.hasConsigne &&
        this.pool.params[this.selectedMode!.consigneName as keyof Params] !==
          -2000
      ) {
        this.probe.seuilMin = Math.min(
          value,
          this.selectedMode!.consigneRange![1] - 2 * this.probeType.gap
        );
        this.pool.params[this.selectedMode!.consigneName as keyof Params] =
          Math.max(
            this.consigneValue,
            this.probe.seuilMin + this.probeType.gap
          );
        const newSeuilMax = Math.max(
          this.probe.seuilMax,
          this.probe.seuilMin + 2 * this.probeType.gap
        );
        if (this.probe.seuilMax === -2000) {
          if (newSeuilMax !== -2000) {
            this.previousSeuilMax = newSeuilMax;
          }
        } else {
          this.probe.seuilMax = newSeuilMax;
        }
      }
    },
    setConsigne(value: number) {
      if (value === -2000) {
        this.pool.params[this.selectedMode!.consigneName as keyof Params] =
          -2000;
        return;
      }

      const adjustedValue = Math.min(
        this.selectedMode!.consigneRange![1] - this.probeType.gap,
        Math.max(
          this.selectedMode!.consigneRange![0] + this.probeType.gap,
          value
        )
      );

      this.pool.params[this.selectedMode!.consigneName as keyof Params] =
        adjustedValue;

      this.probe.seuilMax = Math.max(
        this.probe.seuilMax,
        adjustedValue + this.probeType.gap
      );

      this.probe.seuilMin = Math.min(
        this.probe.seuilMin,
        adjustedValue - this.probeType.gap
      );
    },
    resetDefault() {
      this.setSeuilMin(this.probeType.defaultMin);
      this.setSeuilMax(this.probeType.defaultMax);
      if (this.probeType.defaultConsigne) {
        this.setConsigne(this.probeType.defaultConsigne);
      }
    },
  },
});
</script>

<template>
  <div class="card p-3" v-if="!shouldHideParams">
    <div
      :class="{
        'opacity-50 pointer-events-none':
          probeType.minRoleToChange > (pool.access ?? token.dac),
      }"
    >
      <klereo-spinner
        v-if="loading > 0"
        class="absolute bottom-0 m-3 left-0"
      ></klereo-spinner>
      <div class="flex justify-evenly items-start">
        <div
          style="flex-grow: 1"
          class="text-center partial-border-right"
          v-if="hasConsigne"
        >
          <div class="pb-4">{{ $t("Target") }}</div>
          <klereo-slider
            :class="{ 'pointer-events-none': !editing }"
            variant="vertical"
            :disabled="consigneValue === -2000"
            :initial="
              consigneValue === -2000
                ? previousConsigne ?? probeType.defaultConsigne
                : consigneValue
            "
            :min="selectedMode!.consigneRange![0] || 0"
            :max="selectedMode!.consigneRange![1] || 0"
            :precision="probeType.precision"
            :unit="probeType.unit"
            @change="setConsigne"
          ></klereo-slider>
          <div class="mt-2">
            <label
              ><klereo-checkbox
                :checked="consigneValue !== -2000"
                @change="
                  (checked) => {
                    if (!checked) {
                      previousConsigne = consigneValue;
                    }
                    setConsigne(
                      checked
                        ? previousConsigne ?? probeType.defaultConsigne
                        : -2000
                    );
                  }
                "
              />
              {{ $t("Target") }}</label
            >
          </div>
        </div>
        <div style="flex-grow: 2" class="text-center">
          <div class="pb-4">{{ $t("AlertLevel") }}</div>
          <div class="flex justify-evenly">
            <div>
              <klereo-slider
                :class="{ 'pointer-events-none': !editing }"
                variant="vertical"
                :disabled="probe.seuilMin === -2000"
                :initial="
                  probe.seuilMin === -2000
                    ? previousSeuilMin ?? probeType.sliderRange[0]
                    : probe.seuilMin
                "
                :min="probeType.sliderRange[0]"
                :max="probeType.sliderRange[1]"
                :precision="probeType.precision"
                :unit="probeType.unit"
                @change="setSeuilMin"
              ></klereo-slider>
              <div class="mt-2">
                <label
                  ><klereo-checkbox
                    :checked="probe.seuilMin !== -2000"
                    @change="
                      (checked) => {
                        if (!checked) {
                          previousSeuilMin = probe.seuilMin;
                        }
                        probe.seuilMin = checked
                          ? previousSeuilMin ?? probeType.sliderRange[0]
                          : -2000;
                      }
                    "
                  />
                  {{ $t("MinLevel") }}</label
                >
              </div>
            </div>
            <div>
              <klereo-slider
                :class="{ 'pointer-events-none': !editing }"
                variant="vertical"
                :disabled="probe.seuilMax === -2000"
                :initial="
                  probe.seuilMax === -2000
                    ? previousSeuilMax ?? probeType.sliderRange[1]
                    : probe.seuilMax
                "
                :min="probeType.sliderRange[0]"
                :max="probeType.sliderRange[1]"
                :precision="probeType.precision"
                :unit="probeType.unit"
                @change="setSeuilMax"
              ></klereo-slider>
              <div class="mt-2">
                <label
                  ><klereo-checkbox
                    :checked="probe.seuilMax !== -2000"
                    @change="
                      (checked) => {
                        if (!checked) {
                          previousSeuilMax = probe.seuilMax;
                        }
                        probe.seuilMax = checked
                          ? previousSeuilMax ?? probeType.sliderRange[1]
                          : -2000;
                      }
                    "
                  />
                  {{ $t("MaxLevel") }}</label
                >
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex items-center mt-5 justify-between">
        <div
          class="btn--primary py-1 px-4"
          @click="editing = !editing"
          v-if="editingControls"
        >
          {{ editing ? $t("LockThresholds") : $t("Modif") }}
        </div>

        <div class="text-xs text-right cursor-pointer" @click="resetDefault()">
          {{ $t("ResetToDefaultValues") }}
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.partial-border-right {
  position: relative;
}

.partial-border-right:before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 260px;
  border-right: 2px solid rgba(214, 214, 214, 1);
}
</style>
